// PWA utilities for installation detection and prompts

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  canPrompt: boolean;
}

class PWAManager {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private installStateListeners: ((state: PWAInstallState) => void)[] = [];

  constructor() {
    if (typeof window !== 'undefined') {
      this.initializePWA();
    }
  }

  private initializePWA() {
    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('PWA install prompt available');
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.notifyStateChange();
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('PWA was installed');
      this.deferredPrompt = null;
      this.notifyStateChange();
    });

    // Check if already running as installed PWA
    if (this.isRunningStandalone()) {
      console.log('PWA is running in standalone mode');
    }
  }

  /**
   * Check if the app is running in standalone mode (installed as PWA)
   */
  isRunningStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://')
    );
  }

  /**
   * Check if PWA installation is available
   */
  isInstallAvailable(): boolean {
    return this.deferredPrompt !== null;
  }

  /**
   * Check if the device/browser supports PWA installation
   */
  isPWASupported(): boolean {
    if (typeof window === 'undefined') return false;
    
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  /**
   * Get current PWA installation state
   */
  getInstallState(): PWAInstallState {
    return {
      isInstallable: this.isPWASupported(),
      isInstalled: this.isRunningStandalone(),
      isStandalone: this.isRunningStandalone(),
      canPrompt: this.isInstallAvailable()
    };
  }

  /**
   * Show the PWA installation prompt
   */
  async showInstallPrompt(): Promise<{ outcome: 'accepted' | 'dismissed'; platform: string } | null> {
    if (!this.deferredPrompt) {
      console.warn('No install prompt available');
      return null;
    }

    try {
      await this.deferredPrompt.prompt();
      const choiceResult = await this.deferredPrompt.userChoice;
      
      console.log('PWA install prompt result:', choiceResult.outcome);
      
      if (choiceResult.outcome === 'accepted') {
        this.deferredPrompt = null;
        this.notifyStateChange();
      }
      
      return choiceResult;
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return null;
    }
  }

  /**
   * Register a service worker
   */
  async registerServiceWorker(swPath: string = '/sw.js'): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register(swPath, {
        scope: '/'
      });

      console.log('Service Worker registered successfully:', registration);

      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New service worker available');
              // Notify user about update
              this.notifyServiceWorkerUpdate(registration);
            }
          });
        }
      });

      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  /**
   * Subscribe to PWA install state changes
   */
  onInstallStateChange(callback: (state: PWAInstallState) => void): () => void {
    this.installStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.installStateListeners.indexOf(callback);
      if (index > -1) {
        this.installStateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners about state changes
   */
  private notifyStateChange() {
    const state = this.getInstallState();
    this.installStateListeners.forEach(listener => listener(state));
  }

  /**
   * Handle service worker updates
   */
  private notifyServiceWorkerUpdate(registration: ServiceWorkerRegistration) {
    // This could trigger a user notification about app updates
    console.log('Service worker update available');
    
    // For now, just log. In a real app, you might show a toast notification
    // asking the user if they want to reload to get the latest version
  }

  /**
   * Check if notifications are supported and get permission status
   */
  getNotificationPermission(): NotificationPermission | null {
    if (!('Notification' in window)) {
      return null;
    }
    return Notification.permission;
  }

  /**
   * Request notification permission
   */
  async requestNotificationPermission(): Promise<NotificationPermission | null> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return null;
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return null;
    }
  }
}

// Export singleton instance
export const pwaManager = new PWAManager();

// Utility functions
export const isPWAInstalled = () => pwaManager.isRunningStandalone();
export const isPWASupported = () => pwaManager.isPWASupported();
export const showPWAInstallPrompt = () => pwaManager.showInstallPrompt();
export const registerSW = (path?: string) => pwaManager.registerServiceWorker(path);